import React, {
  useEffect,
  useState,
  useRef,
  RefObject,
  useCallback,
} from 'react';
import { motion } from 'framer-motion';
import classNames from 'classnames';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { DataExtractionTemplateState } from '../../DataExtractionTemplate';
import styles from './Wizard.module.scss';
import { Button, Heading, Icon, IconList, Text } from 'components/core';
import { Inline, Stack } from 'components/layout';

export const wizardStyles = styles;

/**
 * WizardMetaData is a type that defines the structure of metadata used by the Wizard component.
 * Each key in the object represents a step in the wizard, identified by a number.
 *
 * Properties:
 * - `left`: Contains information about the left column of the wizard step.
 *   - `heading` (string): The title or heading for the left column.
 *   - `optional` (boolean, optional): Indicates if the step is optional.
 *   - `description` (JSX.Element | string): A description or additional information for the step.
 *   - `fields` (JSX.Element): The form fields or interactive elements for the step.
 *   - `disableNavigationOnErrors` (string[]): Form fields that must be valid for navigation to be enabled.
 *
 * - `right`: Contains information about the right column of the wizard step.
 *   - `description` (JSX.Element | string): A description or context for the right column.
 *   - `image` (string): The URL of an image to display in the right column.
 */
export type WizardMetaData = Record<
  number,
  {
    left: {
      heading: string;
      optional?: boolean;
      description: JSX.Element | string;
      fields: JSX.Element;
      disableNavigationOnErrors?: string[];
    };
    right: {
      description: JSX.Element | string;
      image: string;
    };
  }
>;

export type WizardPendoData = {
  skip: string;
  back: string;
  next: string;
  done: string;
  setupNow: string;
  editSetup: string;
};

type WizardProps = {
  id: string;
  data: WizardMetaData;
  pendoKey: WizardPendoData;
  isOpen: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  onStepChange?: (currentStep: number) => void;
  onStepsChange?: (totalSteps: number) => void;
};

type ProgressBarProps = {
  currentStep: number;
  totalSteps: number;
};

type ColumnProps = {
  children: React.ReactNode;
  offWhiteVariant?: boolean;
  innerRef?: RefObject<HTMLDivElement>;
};

const ProgressBar = React.memo(
  ({ currentStep, totalSteps }: ProgressBarProps) => {
    const progressWidth = (currentStep / totalSteps) * 100 + '%';
    function setInitialWidth() {
      if (currentStep === 1) {
        return (currentStep / totalSteps) * 100 + '%';
      }

      return ((currentStep - 1) / totalSteps) * 100 + '%';
    }

    return (
      <div className={styles.ProgressBarFrame}>
        <motion.div
          className={styles.ProgressBarFilling}
          animate={{ width: progressWidth }}
          initial={{ width: setInitialWidth() }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          key={currentStep}
        />
      </div>
    );
  }
);

ProgressBar.displayName = 'ProgressBar';

const Column = React.memo(
  ({ children, offWhiteVariant, innerRef }: ColumnProps) => {
    const columnClasses = classNames({
      [styles.Column]: true,
      [styles.OffWhite]: offWhiteVariant,
    });

    return (
      <div ref={innerRef} className={columnClasses}>
        {children}
      </div>
    );
  }
);

Column.displayName = 'Column';

type NavigationProps = {
  currentStep: number;
  totalSteps: number;
  handleClose: () => void;
  pendoKey: WizardPendoData;
  leftColHasScrollBar: boolean;
  firstStep: boolean;
  finalStep: boolean;
  setCurrentStep: (step: number) => void;
  navigationDisabled: boolean;
};

const Navigation = React.memo(
  ({
    currentStep,
    totalSteps,
    handleClose,
    pendoKey,
    leftColHasScrollBar,
    firstStep,
    finalStep,
    setCurrentStep,
    navigationDisabled,
  }: NavigationProps) => {
    return (
      <div
        className={classNames(styles.NavContainer, {
          [styles.NavBoxShadow]: leftColHasScrollBar,
        })}
      >
        <div className={styles.NavContent}>
          <span>
            <Text
              size="xs"
              variant="neutral"
            >{`Step ${currentStep} of ${totalSteps}`}</Text>
            <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          </span>
          <Button
            type="weak"
            variant="ghost"
            className={styles.SkipButton}
            onClick={handleClose}
            data-pendo-key={pendoKey.skip}
            disabled={navigationDisabled}
          >
            Skip
          </Button>
        </div>
        <Inline spacing={4}>
          {!firstStep && (
            <Button
              className={styles.ButtonWidth}
              variant="ghost"
              onClick={() => setCurrentStep(currentStep - 1)}
              data-pendo-key={pendoKey.back}
              disabled={navigationDisabled}
            >
              <Icon icon={IconList.light.faArrowLeft} />
              Back
            </Button>
          )}
          {!finalStep && (
            <Button
              className={styles.ButtonWidth}
              type="brand"
              onClick={() => setCurrentStep(currentStep + 1)}
              data-pendo-key={pendoKey.next}
              disabled={navigationDisabled}
            >
              Next
              <Icon icon={IconList.light.faArrowRight} />
            </Button>
          )}
          {finalStep && (
            <Button
              className={styles.ButtonWidth}
              type="brand"
              onClick={handleClose}
              data-pendo-key={pendoKey.done}
              disabled={navigationDisabled}
            >
              Done
            </Button>
          )}
        </Inline>
      </div>
    );
  }
);

Navigation.displayName = 'Navigation';

const Wizard = ({
  id,
  data,
  pendoKey,
  isOpen,
  onClose,
  onOpen,
  onStepChange,
  onStepsChange,
}: WizardProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = Object.keys(data).length;
  const firstStep = currentStep === 1;
  const finalStep = currentStep === totalSteps;
  const leftColumnRef: RefObject<HTMLDivElement> = useRef(null);
  const [leftColHasScrollBar, setLeftColHasScrollBar] = useState(false);

  const { formState, trigger } = useFormContext<DataExtractionTemplateState>();

  const stepHasErrors: () => boolean = () => {
    // True if validation errors exist for fields listed in disableNavigationOnErrors
    if (data[currentStep].left.disableNavigationOnErrors) {
      return !!data[currentStep].left.disableNavigationOnErrors?.some(
        (error) => error in formState.errors
      );
    } else {
      return false;
    }
  };

  const navigationDisabled = stepHasErrors();

  // Prop callbacks
  useEffect(() => {
    if (onOpen) {
      onOpen();
    }
    if (onStepsChange) {
      onStepsChange(totalSteps);
    }
  }, [onOpen, onStepsChange, totalSteps]);

  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Close modal on escape key press (but not when navigation is blocked)
  useEffect(() => {
    const handleEscape = async (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !navigationDisabled) {
        await trigger(); // run form validations
        if (!stepHasErrors()) handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [navigationDisabled]);

  // Prevent parent scrolling when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      // Reset to first step when closing
      setCurrentStep(1);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Check if left column is scrollable, for conditional box shadow style on nav
  const interventions = useFieldArray<DataExtractionTemplateState>({
    name: 'interventions.interventions',
  });
  const characteristics = useFieldArray<DataExtractionTemplateState>({
    name: 'interventions.characteristics',
  });
  useEffect(() => {
    function updateScrollState() {
      const leftCol = leftColumnRef.current as HTMLDivElement;
      leftCol &&
        setLeftColHasScrollBar(
          leftCol.scrollHeight > leftCol.getBoundingClientRect().height
        );
    }

    updateScrollState();

    window.addEventListener('resize', updateScrollState);
    return () => window.removeEventListener('resize', updateScrollState);
  }, [currentStep, interventions, characteristics]);

  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  const imageSrc = data[currentStep].right.image;

  return (
    <div role="dialog" aria-label={id} className={styles.WizardContainer}>
      <Column innerRef={leftColumnRef}>
        <div className={styles.WizardSection}>
          <section className={styles.WizardContent}>
            <Stack spacing={2}>
              <Heading type="h3">
                {data[currentStep].left.heading}
                {data[currentStep].left.optional ? (
                  <Text variant="weak" component="span">
                    &nbsp;(Optional)
                  </Text>
                ) : (
                  <></>
                )}
              </Heading>
              {data[currentStep].left.description}
              {data[currentStep].left.fields}
            </Stack>
          </section>

          <Navigation
            currentStep={currentStep}
            totalSteps={totalSteps}
            handleClose={handleClose}
            pendoKey={pendoKey}
            leftColHasScrollBar={leftColHasScrollBar}
            firstStep={firstStep}
            finalStep={finalStep}
            setCurrentStep={setCurrentStep}
            navigationDisabled={navigationDisabled}
          />
        </div>
      </Column>

      <Column offWhiteVariant>
        <Stack className={styles.WizardContent} spacing={5}>
          <Heading type="h4">
            How this relates to extracting data from a study
          </Heading>
          {data[currentStep].right.description}
          <img
            className={wizardStyles.Image}
            src={imageSrc}
            alt="context aid"
          />
        </Stack>
      </Column>
    </div>
  );
};

export default Wizard;
